'use client';

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

import { supabase } from '@/lib/supabaseClient';
import { useCambiarPassword } from '@/components/hooks/useCambiarPassword';
import { useTrabajadorConsentimiento } from '@/components/hooks/useTrabajadorConsentimiento';
import { openExternalLink } from '@/components/utils/openExternalLink';
import { PASSWORD_MIN, passwordValida } from '@/components/utils/password';
import PasswordRequisitos from '@/components/ui/PasswordRequisitos';

const SetNewPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [consent, setConsent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    newPassword,
    confirmPassword,
    loading,
    tokenExpired,
    setNewPassword,
    setConfirmPassword,
    cambiar,
  } = useCambiarPassword();

  const {
    registrarConsentimiento,
    comprobarConsentimiento,
    yaAceptado,
    loading: loadingConsent,
  } = useTrabajadorConsentimiento();

  useEffect(() => {
    const init = async () => {
      await supabase.auth.exchangeCodeForSession(window.location.href).catch((e) => {
        console.warn('⚠️ exchangeCodeForSession error:', e);
      });

      const { data } = await supabase.auth.getSession();

      if (data?.session) {
        comprobarConsentimiento();
      } else {
        console.warn('⚠️ No hay sesión tras exchange');
      }
    };

    init();
  }, []);

  const passwordOK =
    passwordValida(newPassword) && newPassword === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar antes de llamar a Supabase: si no, el rechazo llega como error genérico
    if (!passwordValida(newPassword)) {
      toast.error(t('password.errorWeak', { min: PASSWORD_MIN }));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t('password.errorMatch'));
      return;
    }

    const ok = await cambiar();
    if (!ok) return;

    // Registrar consentimiento si aún no lo ha aceptado (necesita la sesión aún viva)
    if (yaAceptado === false) {
      await registrarConsentimiento();
    }

    // Cerramos la sesión de recuperación y mandamos a login: evita que la
    // rutina de bienvenida de Login intente resolver la sesión recién creada
    // y se quede en bucle. El usuario entra con la contraseña nueva.
    await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
    navigate('/', { replace: true });
  };

  const isSaving = loading || loadingConsent;

  /* TOKEN CADUCADO */
  if (tokenExpired) {
    return (
      <div className="flex flex-col min-h-screen items-center justify-center text-center bg-gray-50 dark:bg-black px-6">
        <Card className="w-full max-w-md rounded-md shadow-lg border dark:border-white/10 dark:bg-[#1c1c1e]">
          <CardContent className="p-8 space-y-6">
            <h2 className="text-2xl font-bold text-[#103254] dark:text-white">
              {t('setNewPassword.expiredTitle')}
            </h2>
            <p className="text-gray-700 dark:text-gray-300">{t('setNewPassword.expiredText')}</p>
            <Button
              onClick={() => navigate('/recuperar')}
              className="w-full rounded-xl bg-[#103254] hover:bg-[#0d2742] text-white font-semibold shadow-md dark:bg-[#288E7F] dark:hover:bg-[#1e7a6e]"
            >
              {t('setNewPassword.expiredButton')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  /* FORMULARIO */
  return (
    <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-black">
      {/* HEADER */}
      <div
        className="flex flex-col items-center justify-center text-center px-6 h-[30vh] dark:hidden"
        style={{ backgroundColor: '#103254' }}
      >
        <img
          src="https://ijolbckmgiwomizpzrae.supabase.co/storage/v1/object/public/bookoll/logos/logobookoll.png"
          alt="Bookoll Logo"
          className="h-16 sm:h-20 mb-4"
        />
        <h1 className="text-xl sm:text-2xl text-white font-semibold">
          {t('setNewPassword.heroTitle')}
        </h1>
        <p className="text-white/80 text-sm sm:text-base mt-2 max-w-md">
          {t('setNewPassword.heroSubtitle')}
        </p>
      </div>
      {/* HEADER modo oscuro: negro con resplandor teal, funde con el resto de la app */}
      <div
        className="hidden dark:flex flex-col items-center justify-center text-center px-6 h-[30vh] relative overflow-hidden"
        style={{
          background: `
            radial-gradient(ellipse at 15% 30%, rgba(40,142,127,0.3) 0%, transparent 55%),
            radial-gradient(ellipse at 85% 70%, rgba(16,50,84,0.5) 0%, transparent 55%),
            linear-gradient(160deg, #050807 0%, #0a1a16 30%, #103254 70%, #000000 100%)
          `,
        }}
      >
        <img
          src="https://ijolbckmgiwomizpzrae.supabase.co/storage/v1/object/public/bookoll/logos/logobookoll.png"
          alt="Bookoll Logo"
          className="relative z-10 h-16 sm:h-20 mb-4"
        />
        <h1 className="relative z-10 text-xl sm:text-2xl text-white font-semibold">
          {t('setNewPassword.heroTitle')}
        </h1>
        <p className="relative z-10 text-white/80 text-sm sm:text-base mt-2 max-w-md">
          {t('setNewPassword.heroSubtitle')}
        </p>
      </div>

      {/* FORM */}
      <div className="flex flex-1 items-center justify-center px-4 py-8">
        <Card className="w-full max-w-md rounded-md shadow-lg border dark:border-white/10 dark:bg-[#1c1c1e]">
          <CardContent className="p-8 space-y-6">
            <h2 className="text-2xl font-bold text-center text-[#103254] dark:text-white">
              {t('setNewPassword.formTitle')}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="newPassword" className="dark:text-gray-300">
                  {t('setNewPassword.newPassword')}
                </Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    placeholder={t('password.placeholder')}
                    className="rounded-xl pr-10 dark:bg-black/40 dark:border-white/10 dark:text-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-white/40 hover:text-gray-600 dark:hover:text-white/70"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <PasswordRequisitos password={newPassword} />
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="dark:text-gray-300">
                  {t('setNewPassword.confirmPassword')}
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="rounded-xl pr-10 dark:bg-black/40 dark:border-white/10 dark:text-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    tabIndex={-1}
                    aria-label={
                      showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-white/40 hover:text-gray-600 dark:hover:text-white/70"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {confirmPassword.length > 0 && newPassword !== confirmPassword && (
                  <p className="mt-2 text-xs text-red-500">
                    {t('password.errorMatch')}
                  </p>
                )}
              </div>

              {/* Visible si aún no ha aceptado los términos, independientemente del flujo */}
              {yaAceptado === false && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-gray-50 border border-gray-100 dark:bg-white/5 dark:border-white/10">
                  <Switch checked={consent} onCheckedChange={setConsent} />
                  <Label className="text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                    Acepto los{' '}
                    <button
                      type="button"
                      onClick={() => openExternalLink('https://bookoll.com/terms')}
                      className="underline text-[#103254] dark:text-white font-medium text-xs leading-relaxed"
                    >
                      Términos y condiciones
                    </button>{' '}
                    y la{' '}
                    <button
                      type="button"
                      onClick={() => openExternalLink('https://bookoll.com/privacy-policy')}
                      className="underline text-[#103254] dark:text-white font-medium text-xs leading-relaxed"
                    >
                      Política de privacidad
                    </button>{' '}
                    de Bookoll.
                  </Label>
                </div>
              )}

              <Button
                type="submit"
                className="w-full rounded-xl bg-[#103254] hover:bg-[#0d2742] text-white font-semibold shadow-md disabled:opacity-40 disabled:cursor-not-allowed dark:bg-[#288E7F] dark:hover:bg-[#1e7a6e]"
                disabled={
                  isSaving || !passwordOK || (yaAceptado === false && !consent)
                }
              >
                {isSaving
                  ? t('setNewPassword.saving')
                  : t('setNewPassword.submit')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* FOOTER */}
      <footer className="bg-gray-100 border-t py-6 text-center text-sm text-gray-600 dark:bg-black dark:border-white/10 dark:text-gray-500">
        © {new Date().getFullYear()} Bookoll
      </footer>
    </div>
  );
};

export default SetNewPassword;